import { Col, Row } from 'react-awesome-styled-grid';
import styled from 'styled-components';
import { Button, P1, Modal } from 'pfm-ui-components';
import { pxToRem } from '../../../../styles/utils';
import { up } from 'styled-breakpoints';
import { No, Yes, Ok } from '../../../../constants/common';
import { KYC_ERRORS, KYC_MODALS, KYC_MODAL_COMMANDS } from '../../../../constants/Kyc';
import GTM from '../../../../GTM';
import { useEffect } from 'react';

interface KycModalProps {
  handleModalCommand: Function;
  modalType: string;
  modalMsg?: string | null | undefined;
}

const getModalMessage = (modalType: string | number, modalMsg?: string | null | undefined) => {
  switch (modalType) {
    case KYC_MODALS.MF_KYC_CANCEL:
      return (
        <span>
          By selecting <span className="bold">“Cancel”</span> you will not be allowed to proceed ahead. Are you sure?
        </span>
      );

    case KYC_MODALS.FATCA_CANCEL:
      return (
        <span>
          You have chosen <span className="bold">‘Cancel’</span>. You won't be able to proceed with the investment. Are
          you sure?
          <span className="bold"> Yes | No</span>
        </span>
      );

    case KYC_MODALS.MFL_WARNING:
      return (
        <span>
          To invest in mutual funds, you need to complete your <span className="bold">MF KYC verification</span>. Kindly
          confirm the details shown previously or visit the nearest Axis Bank branch or connect with your relationship
          manager
        </span>
      );

    case KYC_MODALS.TRANSACTION_NOT_ALLOWED:
      return <span>Transaction not allowed</span>;

    case KYC_MODALS.REGISTRATION_NOT_ALLOWED:
      return <span>Registration not allowed</span>;

    case 7036:
      return (
        <span>
          <p>Oops, your CKYC details are not completed as per bank records. </p>
          <p>
            We request you to visit the nearest Axis Bank branch to complete your KYC and activate for online mutual
            fund investment.
          </p>
          <p>
            Kindly carry the following documents when you visit the branch:
            <br /> PAN Card <br /> Address Proof
            <br /> One passport size photo
          </p>
          <p>
            Once your KYC verification is completed, we will inform you post which you can invest online in mutual
            funds.
          </p>
        </span>
      );

    case 7037:
      return (
        <span>
          <p>Oops, you are due for ReKYC.</p>
          <p>
          To complete your KYC online, please login to Axis Bank mobile app and follow the path: Services &amp; Support &gt; Services &gt; My Profile &gt; Update KYC.
          </p>
          <p>
          Or login to Axis Bank internet banking and follow the path: Services &gt; Confirm KYC.
          </p>
          <p>
            Once your KYC verification is completed, we will inform you post which you can invest online in mutual
            funds.
          </p>
        </span>
      );

    case 7038:
      return (
        <span>
          <p>
            Oops, your KYC seems to be incomplete. KYC or Know Your Customer is a one-time process which is required to
            be completed for investing in mutual funds
          </p>
          <p>
            We request you to visit the nearest Axis Bank branch to complete your KYC and activate for online mutual
            fund investment.
          </p>
          <p>
            Kindly carry the following documents when you visit the branch:
            <br /> PAN Card <br /> Address Proof
            <br /> One passport size photo
          </p>
          <p>
            Once your KYC verification is completed, we will inform you post which you can invest online in mutual
            funds.
          </p>
        </span>
      );

    default:
      // modalType is a error code
      return <span>{KYC_ERRORS[modalType] || modalMsg || 'Oops! Something went wrong.'}</span>;
  }
};

const getPrimaryLabel = (modalType: string) => {
  switch (modalType) {
    case KYC_MODALS.MF_KYC_CANCEL:
    case KYC_MODALS.FATCA_CANCEL:
      return No;
    default:
      return Ok;
  }
};

const getSecondaryLabel = (modalType: string) => {
  switch (modalType) {
    case KYC_MODALS.MF_KYC_CANCEL:
    case KYC_MODALS.FATCA_CANCEL:
      return Yes;
    default:
      return null;
  }
};

const onModalButtonPress = (handleModalCommand: Function, modalType: string, isPrimaryButton?: boolean) => {
  switch (modalType) {
    case KYC_MODALS.MF_KYC_CANCEL:
      if (isPrimaryButton) {
        handleModalCommand(KYC_MODAL_COMMANDS.CLOSE_MODAL);
      } else {
        handleModalCommand(KYC_MODAL_COMMANDS.NAVIGATE_TO_MFL);
      }
      break;
    case KYC_MODALS.FATCA_CANCEL:
      if (isPrimaryButton) {
        GTM.registration.fatcaDeclarationModalButtonClick(No);
        handleModalCommand(KYC_MODAL_COMMANDS.CLOSE_MODAL);
      } else {
        GTM.registration.fatcaDeclarationModalButtonClick(Yes);
        handleModalCommand(KYC_MODAL_COMMANDS.SHOW_FATCA_DECLINED);
      }
      break;

    case KYC_MODALS.MFL_WARNING:
      handleModalCommand();
      break;
    default:
      handleModalCommand(KYC_MODAL_COMMANDS.NAVIGATE_TO_MFL_NO_MODAL);
      break;
  }
};

const ModalFooter = ({ modalType, handleModalCommand }) => {
  const hasSecondaryButton = !!getSecondaryLabel(modalType);
  return (
    <ActionsWrapper>
      {hasSecondaryButton && (
        <ButtonWrapper xs={5.5} md={1.5}>
          <Button
            onClick={() => {
              onModalButtonPress(handleModalCommand, modalType);
            }}
          >
            {getSecondaryLabel(modalType)}
          </Button>
        </ButtonWrapper>
      )}
      <ButtonWrapper xs={hasSecondaryButton ? 5.5 : 12} md={1.5} offset={{ xs: hasSecondaryButton ? 1 : 0, md: 0 }}>
        <Button
          primary
          onClick={() => {
            onModalButtonPress(handleModalCommand, modalType, true);
          }}
        >
          {getPrimaryLabel(modalType)}
        </Button>
      </ButtonWrapper>
    </ActionsWrapper>
  );
};

export default function KycModals(props: KycModalProps) {
  const { handleModalCommand, modalType, modalMsg } = props;
  if (!modalType) {
    return null;
  }
  useEffect(() => {
    switch (modalType) {
      case KYC_MODALS.FATCA_CANCEL:
        GTM.registration.fatcaDeclarationModalOnLoad();
        break;
    }
  }, []);

  return (
    <Modal
      id="cancel-kyc"
      showHeader={false}
      mode="prompt"
      FooterComponent={<ModalFooter modalType={modalType} handleModalCommand={handleModalCommand} />}
    >
      <StyledP1>{getModalMessage(modalType, modalMsg)}</StyledP1>
    </Modal>
  );
}

const StyledP1 = styled(P1)`
  .bold {
    font-weight: bold;
  }
`;

const ActionsWrapper = styled(Row)`
  display: flex;
  justify-content: flex-end;
  padding: 0;
`;

const ButtonWrapper = styled(Col)`
  padding: 0;
  ${up('md')} {
    margin-left: ${pxToRem(16)};
  }
`;
